import { createContext, useContext, useEffect, useState } from "react";
import { api, getOrCreateDeviceId } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!sessionStorage.getItem("access_token") && !localStorage.getItem("access_token")) {
        setUser(false);
        setLoading(false);
        return;
      }
      // Bug fix: refreshing ANY page used to bounce straight to the home/
      // login screen instead of staying put whenever this one-shot /auth/me
      // check failed for ANY reason - including a plain network blip, a slow
      // backend cold start, or a dropped mobile connection, none of which
      // mean the session is actually invalid. A real 401 already gets one
      // silent refresh-and-retry inside api.js's interceptor before it ever
      // reaches this catch, so by the time we see a 401/403 here the session
      // genuinely is invalid and logging out is correct. But a network/
      // timeout or 5xx response means the backend is unavailable, not that
      // the session is invalid. Keep the current route in its loading state
      // and retry with a bounded backoff; only 401/403 may log the user out.
      let attempt = 0;
      while (!cancelled) {
        try {
          const { data } = await api.get("/auth/me");
          if (cancelled) return;
          setUser(data);
          setLoading(false);
          return;
        } catch (err) {
          const status = Number(err?.response?.status || 0);
          const isAuthFailure = status === 401 || status === 403;
          if (isAuthFailure) {
            if (!cancelled) {
              setUser(false);
              setLoading(false);
            }
            return;
          }
          // A refresh must stay on its requested route while the backend is
          // temporarily starting, reconnecting to MongoDB, or returning a
          // transient 5xx response. Keep retrying auth bootstrap instead of
          // falsely declaring the session logged out and redirecting to `/`.
          attempt += 1;
          await new Promise((r) => setTimeout(r, Math.min(5000, 600 * attempt)));
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // api.js can't reach into this context directly when a request fails with
  // no way to recover (no refresh token, or the refresh itself failed) - it
  // clears storage and dispatches this event instead. Updating user state
  // here lets the existing routing (ProtectedRoute / HomeRoute, unchanged)
  // decide where that lands: a protected screen redirects to login as
  // before, a public page just reflects being logged out without any
  // forced navigation.
  useEffect(() => {
    const onInvalidated = () => setUser(false);
    window.addEventListener("rp:auth-invalidated", onInvalidated);
    return () => window.removeEventListener("rp:auth-invalidated", onInvalidated);
  }, []);

  const login = async (identifier, password, rememberMe = false, options = {}) => {
    const platform = typeof window !== "undefined" && window.RpSocietyAndroid ? "android" : "web";
    const { data } = await api.post("/auth/login", {
      identifier, password, rememberMe,
      deviceId: getOrCreateDeviceId(),
      deviceLabel: typeof navigator !== "undefined" ? navigator.userAgent?.slice(0, 120) : "",
      platform,
      ...options,
    });
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    // Ravi: token-expiry fix -- keep the refresh_token alongside the access
    // token so api.js can silently renew the session in the background
    // instead of guards/residents getting locked out mid-shift. This was
    // previously a no-op: /auth/login never actually included refresh_token
    // in its response (only /auth/refresh did), so this line never ran on
    // initial login and api.js's interceptor always saw "no refresh token
    // available" and skipped straight to forcing a re-login on the first
    // expired access token - that was the actual root cause of the 10-12h
    // logout even though a refresh endpoint existed. Backend now returns it
    // from /auth/login too.
    if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    sessionStorage.removeItem("access_token");
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    sessionStorage.removeItem("refresh_token");
    localStorage.removeItem("hotel_access_token");
    localStorage.removeItem("shopper_access_token");
    sessionStorage.removeItem("rp-home-scroll-target");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
